import { textColorFields } from '../_shared/textColor.js';
import { universalFields } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
import { patternFields } from '../_shared/pattern.js';
/**
 * logo-list — Shrine "Featured on" at CONFIG-BY-CONFIG parity. A heading (+ the shared word-HIGHLIGHT
 * engine and an optional pattern background) over a row of brand logos that reads as a centered, wrapping
 * flex row on desktop and flips to an autoplay-capable, dotted SLIDER (or a fixed-column grid) on mobile.
 * Each LOGO is an inline child block (LEGACY model, like testimonials): an uploaded image + optional link.
 *
 * SHRINE PARITY: every rendered knob of Shrine's `logo-list.liquid` is implemented here and wired in
 * Component.tsx — visibility, the heading + two highlight engines + heading size, gray logos, the pattern
 * cluster, desktop layout + logo height, and the full mobile block (columns, logo height, slider on/off,
 * autoplay + speed, dots). The Shopify-platform-only knobs are DEFERRED with justification (see the bottom
 * of this file and Component.tsx): `display_id` (section-merge UI helper), the six `color_scheme` selects
 * map onto our 3-way `colorScheme`/scheme tokens, the `custom_*_image_background` pair (Shopify img_url
 * pipeline), reveal/scroll animations, and the live Splide autoplay/loop JS (a client island — the dots +
 * native scroll-snap render, and the JS-only knobs ride along as data-* for a future island). The
 * `line-1/2/3` heading decorations use CSS text-decoration rather than Shrine's SVG glyph assets.
 */
export const logoListSchema = {
    type: 'logo-list',
    name: 'Featured on',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── General (Shrine top-level settings) ──────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Visibility',
            group: 'General',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'Always display' },
                { value: 'desktop-hidden', label: 'Hide on desktop' },
                { value: 'mobile-hidden', label: 'Hide on mobile' },
            ],
        },
        {
            id: 'title',
            type: 'richtext',
            label: 'Heading',
            group: 'General',
            default: { format: 'html', html: '<p>Featured on</p>' },
            info: 'Bold a word to apply highlight 1, italic to apply highlight 2.',
        },
        // ── Heading highlight engines 1 (bold) & 2 (italic), grouped under General. ─
        ...headingHighlightFields(1, 'General').map((f) => f.id === 'headingHighlight1'
            ? { ...f, info: 'Bold a word in the heading to style it with this.' }
            : f),
        ...headingHighlightFields(2, 'General').map((f) => f.id === 'headingHighlight2'
            ? { ...f, info: 'Italicize a word in the heading to style it with this.' }
            : f),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'General',
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'range',
            label: 'Custom desktop size',
            group: 'General',
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'range',
            label: 'Custom mobile size',
            group: 'General',
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'grayLogos',
            type: 'checkbox',
            label: 'Gray logos',
            group: 'General',
            default: false,
        },
        // ── Background pattern (shared engine — Shrine pattern_bg cluster). ────────
        ...patternFields('Background pattern'),
        // ── Desktop (Shrine HEADER: Desktop) ─────────────────────────────────────
        {
            id: 'layout',
            type: 'select',
            label: 'Desktop layout',
            group: 'Desktop',
            default: 'horizontal',
            info: 'Horizontal places the heading beside the logos; Vertical stacks it above them.',
            options: [
                { value: 'vertical', label: 'Vertical' },
                { value: 'horizontal', label: 'Horizontal' },
            ],
        },
        {
            id: 'desktopLogoHeight',
            type: 'range',
            label: 'Desktop logo height',
            group: 'Desktop',
            min: 30,
            max: 150,
            step: 10,
            unit: 'px',
            default: 50,
        },
        // ── Mobile (Shrine HEADER: Mobile) ───────────────────────────────────────
        {
            id: 'columnsMobile',
            type: 'range',
            label: 'Columns on mobile',
            group: 'Mobile',
            min: 1,
            max: 4,
            step: 1,
            default: 2,
        },
        {
            id: 'mobileLogoHeight',
            type: 'range',
            label: 'Mobile logo height',
            group: 'Mobile',
            min: 30,
            max: 150,
            step: 10,
            unit: 'px',
            default: 50,
        },
        {
            id: 'mobileSlider',
            type: 'checkbox',
            label: 'Enable mobile slider',
            group: 'Mobile',
            default: true,
            info: 'When off, logos lay out as a fixed-column grid on mobile.',
        },
        {
            id: 'autoplay',
            type: 'checkbox',
            label: 'Autoplay',
            group: 'Mobile',
            default: true,
            info: 'The looping autoplay carousel needs a client island; the dots + swipe render.',
            visibleWhen: { field: 'mobileSlider', equals: true },
        },
        {
            id: 'autoplaySpeed',
            type: 'range',
            label: 'Autoplay speed',
            group: 'Mobile',
            min: 1,
            max: 15,
            step: 0.5,
            unit: 's',
            default: 5,
            visibleWhen: { field: 'mobileSlider', equals: true },
        },
        {
            id: 'showDots',
            type: 'checkbox',
            label: 'Show dots',
            group: 'Mobile',
            default: true,
            visibleWhen: { field: 'mobileSlider', equals: true },
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // ── Section (universal: color scheme, padding_top/bottom, width) ─────────
        ...universalFields,
    ],
    // The LOGO inline block (Shrine `logo`): an uploaded image + optional link.
    blocks: [
        {
            type: 'logo',
            name: 'Logo',
            max: 20,
            fields: [
                { id: 'image', type: 'image', label: 'Logo' },
                { id: 'link', type: 'link', label: 'Link' },
                {
                    id: 'targetBlank',
                    type: 'checkbox',
                    label: 'Open link in a new tab',
                    default: false,
                },
            ],
        },
    ],
    maxBlocks: 20,
};
export const logoListPresets = [
    {
        name: 'Featured on',
        settings: {
            visibility: 'always-display',
            title: { format: 'html', html: '<p>Featured on</p>' },
            headingSize: 'h1',
            headingHighlight1: 'solid-color',
            headingHighlight1Color: { ref: 'theme', token: 'accent' },
            grayLogos: false,
            layout: 'horizontal',
            desktopLogoHeight: 50,
            columnsMobile: 2,
            mobileLogoHeight: 50,
            mobileSlider: true,
            autoplay: true,
            autoplaySpeed: 5,
            showDots: true,
            // Clean LIGHT band; Shrine's default logo-list padding is 36px desktop / 27px mobile (0.75×).
            colorScheme: 'light',
            width: 'wide',
            paddingTop: 36,
            paddingBottom: 36,
            mobilePaddingTop: 27,
            mobilePaddingBottom: 27,
        },
        // Three empty logo blocks — each renders Shrine's neutral image placeholder until the seller uploads a
        // logo (mirrors Shrine's 3-logo "Featured on" preset).
        blocks: [{ type: 'logo' }, { type: 'logo' }, { type: 'logo' }],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const logoListMeta = {
    label: 'Featured on',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map